import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import envVariables from "src/lib/envVariables";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import reportErrorSentry from "src/lib/reportErrorSentry";
import {
  ApiController,
  ApiControllerConfig
} from "lib/ApiController/ApiController";
import { setRequestGuard } from "lib/ApiController/requestGuard";
import { Blac } from "blac-next";
import AssumedIdentityCubit from "src/state/AssumedIdentityCubit/AssumedIdentityCubit";
import type AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import type AppVersionBloc from "src/hybrid/AppVersionBloc";

const REFRESH_TOKEN_ENDPOINT = "/v1/users/token/refresh";
export const ASSUMED_IDENTITY_EXCHANGE_ENDPOINT =
  "/v1/auth/identity/assume/exchange";

/**
 * POSTs that only obtain tokens. The exchange must pass when a tab still holds
 * an earlier read-only session, or a new hand-over could never replace it.
 */
const TOKEN_ENDPOINTS = [
  REFRESH_TOKEN_ENDPOINT,
  ASSUMED_IDENTITY_EXCHANGE_ENDPOINT
];

export const guardAssumedIdentityRequest = ({
  method,
  url
}: {
  method: string;
  url: URL;
}): boolean => {
  if (TOKEN_ENDPOINTS.some((endpoint) => url.pathname.endsWith(endpoint))) {
    return true;
  }
  const assumedIdentity = Blac.getBloc(AssumedIdentityCubit);
  if (assumedIdentity.allowsRequest(method)) {
    return true;
  }
  assumedIdentity.recordBlocked(method, url.pathname);
  return false;
};

/*
 * Lazy ApiController instance
 */
let apiControllerInstance: ApiController | null = null;

/*
 * Get the ApiController instance
 */
export function getApiController(options?: {
  authenticationState: AuthenticationBloc;
  appVersionState: AppVersionBloc;
}): ApiController {
  if (!apiControllerInstance) {
    if (!options) {
      const error = new Error("getApiController: options are required");
      reportErrorSentry(error);
      throw error;
    }
    const config: ApiControllerConfig = {
      refreshTokenEndpoint: REFRESH_TOKEN_ENDPOINT,
      ignoreTraceForSuccessResponse: [/track$/, /features$/, /status$/],
      requestsWithoutAuth: [
        "/v1/users/anonymous",
        "/v1/users/me/password/reset",
        "/v1/users/me/password/reset/confirm",
        "/v1/users/email/verification/initiate",
        "/v1/users/email/verification/confirm",
        "/v1/users/login",
        "/v1/auth/saml2/exchange",
        "/v1/integrations/transcarent/exchange",
        ASSUMED_IDENTITY_EXCHANGE_ENDPOINT
      ],
      requestsWithExpected401: [
        "/v1/users/me/mfa",
        "/v1/users/me/password/reset/confirm"
      ],
      priorityRequestsWithAuth: [REFRESH_TOKEN_ENDPOINT],
      additionalApiHosts: envVariables.EXTRA_API_HOSTS,
      loggingEnabled: featureFlags.getFlag(FeatureFlagName.loggingApiCache),
      clientId: `web|${options.appVersionState.currentVersionString}`,
      logBreadcrumb: logSentryBreadcrumb,
      reportError: reportErrorSentry,
      authState: options.authenticationState
    };

    apiControllerInstance = new ApiController(config);
    apiControllerInstance.patchGlobalFetch();
    setRequestGuard(guardAssumedIdentityRequest);
  }
  return apiControllerInstance;
}

export function processRequestQueue() {
  return getApiController().processRequestQueue();
}

export function logAverageTimes() {
  return getApiController().logAverageTimes();
}

// For testing purposes only - reset the singleton
export function resetApiControllerForTesting() {
  apiControllerInstance = null;
}
